import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { EReviewDueState, type TReviewReminderRecipient } from '@onesource/schemas';
import { DateTime } from 'luxon';
import { FC, useMemo } from 'react';

import { URGENCY_STYLE } from './review-urgency';

export interface TReminderFormOption {
  formId: string;
  formTitle: string;
  dueAt: Date | string | null;
  isOverdue: boolean;
  students: number;
  /** Already reminded about this form in the last 24 hours. */
  skipped: number;
}

/** The forms in a bulk preview, each with how many students have not submitted it. */
export const toFormOptions = (recipients: TReviewReminderRecipient[]): TReminderFormOption[] => {
  const byForm = new Map<string, TReminderFormOption>();
  for (const r of recipients) {
    if (!r.form) {
      continue;
    }
    const option = byForm.get(r.form.formId) ?? {
      formId: r.form.formId,
      formTitle: r.form.formTitle,
      dueAt: r.form.dueAt,
      isOverdue: r.form.due === EReviewDueState.overdue,
      students: 0,
      skipped: 0,
    };
    option.students += 1;
    option.skipped += r.skipped ? 1 : 0;
    byForm.set(r.form.formId, option);
  }
  return [...byForm.values()].sort((a, b) => Number(b.isOverdue) - Number(a.isOverdue) || b.students - a.students);
};

interface Props {
  recipients: TReviewReminderRecipient[];
  value: string | null;
  onChange: (formId: string) => void;
}

/** Step 1 of a bulk student reminder: which form to remind about. */
export const ReviewReminderFormStep: FC<Props> = ({ recipients, value, onChange }) => {
  const options = useMemo(() => toFormOptions(recipients), [recipients]);
  return (
    <Box
      role="radiogroup"
      aria-label="Form to remind about"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
    >
      {options.map(option => {
        const isOn = option.formId === value;
        const due = option.dueAt
          ? `${option.isOverdue ? 'was due' : 'due'} ${DateTime.fromJSDate(new Date(option.dueAt)).toLocaleString({ day: 'numeric', month: 'short' })}`
          : 'no due date';
        return (
          <ButtonBase
            key={option.formId}
            role="radio"
            tabIndex={0}
            aria-checked={isOn}
            onClick={() => onChange(option.formId)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1,
              py: 1,
              textAlign: 'left',
              bgcolor: isOn ? 'action.selected' : undefined,
              '& + &': { borderTop: '1px solid', borderColor: 'divider' },
            }}
          >
            <Radio checked={isOn} tabIndex={-1} size="small" inputProps={{ 'aria-hidden': true }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {option.formTitle}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: option.isOverdue ? URGENCY_STYLE.overdue.text : 'text.secondary',
                  fontWeight: option.isOverdue ? 700 : 400,
                }}
              >
                {due}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {option.students} {option.students === 1 ? 'student' : 'students'}
              </Typography>
              {option.skipped > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {option.skipped} reminded today
                </Typography>
              )}
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
};
